/**
 * ReservationBadge - Vendor Orders Style Theme
 *
 * Consistent badge styling matching order status chips
 */

import React from 'react';
import { Chip } from '@mui/material';
import {
  HourglassEmpty as PendingIcon,
  CheckCircle as ConfirmedIcon,
  EventSeat as SeatedIcon,
  Done as CompletedIcon,
  Cancel as CancelledIcon,
  DoNotDisturb as NoShowIcon,
  Schedule as TimeIcon,
  People as PeopleIcon,
  Notifications as NotifyIcon,
} from '@mui/icons-material';
import type { ReservationStatus, WaitlistStatus } from '../../types/reservation.types';

// ============================================================================
// RESERVATION BADGE
// ============================================================================

interface ReservationBadgeProps {
  status: ReservationStatus;
  size?: 'small' | 'medium';
  showIcon?: boolean;
  className?: string;
}

export const ReservationBadge: React.FC<ReservationBadgeProps> = ({
  status,
  size = 'small',
  showIcon = true,
  className = '',
}) => {
  const getStatusConfig = (status: ReservationStatus) => {
    // Colors matching VendorOrders status chips
    const configs: Record<ReservationStatus, {
      color: string;
      bgColor: string;
      borderColor: string;
      icon: JSX.Element;
      label: string
    }> = {
      PENDING: {
        color: 'var(--status-warn-text)',
        bgColor: 'var(--status-warn-bg)',
        borderColor: 'var(--status-warn-border)',
        icon: <PendingIcon sx={{ fontSize: 14 }} />,
        label: 'Pending',
      },
      CONFIRMED: {
        color: 'var(--action)',
        bgColor: '#CCFBF1', /* basil-todo */
        borderColor: '#5EEAD4', /* basil-todo */
        icon: <ConfirmedIcon sx={{ fontSize: 14 }} />,
        label: 'Confirmed',
      },
      SEATED: {
        color: 'var(--status-ok)',
        bgColor: 'var(--status-ok-bg)',
        borderColor: '#6EE7B7', /* basil-todo */
        icon: <SeatedIcon sx={{ fontSize: 14 }} />,
        label: 'Seated',
      },
      COMPLETED: {
        color: 'var(--text-secondary)',
        bgColor: 'var(--bg-sunken)',
        borderColor: 'var(--border-default)',
        icon: <CompletedIcon sx={{ fontSize: 14 }} />,
        label: 'Completed',
      },
      CANCELLED: {
        color: 'var(--status-bad)',
        bgColor: 'var(--status-bad-bg)',
        borderColor: 'var(--status-bad-border)',
        icon: <CancelledIcon sx={{ fontSize: 14 }} />,
        label: 'Cancelled',
      },
      NO_SHOW: {
        color: 'var(--status-bad-text)',
        bgColor: 'var(--status-bad-bg)',
        borderColor: 'var(--status-bad-border)',
        icon: <NoShowIcon sx={{ fontSize: 14 }} />,
        label: 'No Show',
      },
    };

    return configs[status];
  };

  const config = getStatusConfig(status);

  return (
    <Chip
      label={config.label}
      size={size}
      icon={showIcon ? config.icon : undefined}
      className={className}
      sx={{
        backgroundColor: config.bgColor,
        color: config.color,
        fontWeight: 600,
        fontSize: '0.6875rem',
        height: size === 'small' ? 22 : 28,
        border: `1px solid ${config.borderColor}`,
        '& .MuiChip-icon': {
          color: config.color,
          marginLeft: '6px',
        },
        '& .MuiChip-label': {
          paddingLeft: showIcon ? '4px' : '8px',
          paddingRight: '8px',
        },
      }}
    />
  );
};

// ============================================================================
// WAITLIST BADGE
// ============================================================================

interface WaitlistBadgeProps {
  status: WaitlistStatus;
  size?: 'small' | 'medium';
  showIcon?: boolean;
  className?: string;
}

export const WaitlistBadge: React.FC<WaitlistBadgeProps> = ({
  status,
  size = 'small',
  showIcon = true,
  className = '',
}) => {
  const getStatusConfig = (status: WaitlistStatus) => {
    const configs: Record<WaitlistStatus, {
      color: string;
      bgColor: string;
      borderColor: string;
      icon: JSX.Element;
      label: string
    }> = {
      WAITING: {
        color: 'var(--status-warn-text)',
        bgColor: 'var(--status-warn-bg)',
        borderColor: 'var(--status-warn-border)',
        icon: <PendingIcon sx={{ fontSize: 14 }} />,
        label: 'Waiting',
      },
      NOTIFIED: {
        color: 'var(--accent-alt)',
        bgColor: 'var(--accent-alt-bg)',
        borderColor: '#C4B5FD', /* basil-todo */
        icon: <NotifyIcon sx={{ fontSize: 14 }} />,
        label: 'Notified',
      },
      SEATED: {
        color: 'var(--status-ok)',
        bgColor: 'var(--status-ok-bg)',
        borderColor: '#6EE7B7', /* basil-todo */
        icon: <SeatedIcon sx={{ fontSize: 14 }} />,
        label: 'Seated',
      },
      REMOVED: {
        color: 'var(--text-secondary)',
        bgColor: 'var(--bg-sunken)',
        borderColor: 'var(--border-default)',
        icon: <CancelledIcon sx={{ fontSize: 14 }} />,
        label: 'Removed',
      },
      EXPIRED: {
        color: 'var(--status-bad)',
        bgColor: 'var(--status-bad-bg)',
        borderColor: 'var(--status-bad-border)',
        icon: <NoShowIcon sx={{ fontSize: 14 }} />,
        label: 'Expired',
      },
    };

    return configs[status];
  };

  const config = getStatusConfig(status);

  return (
    <Chip
      label={config.label}
      size={size}
      icon={showIcon ? config.icon : undefined}
      className={className}
      sx={{
        backgroundColor: config.bgColor,
        color: config.color,
        fontWeight: 600,
        fontSize: '0.6875rem',
        height: size === 'small' ? 22 : 28,
        border: `1px solid ${config.borderColor}`,
        '& .MuiChip-icon': {
          color: config.color,
          marginLeft: '6px',
        },
        '& .MuiChip-label': {
          paddingLeft: showIcon ? '4px' : '8px',
          paddingRight: '8px',
        },
      }}
    />
  );
};

// ============================================================================
// TABLE RESERVATION INDICATOR (for TableCard)
// ============================================================================

interface TableReservationIndicatorProps {
  reservation?: {
    status: ReservationStatus;
    startTime: string;
    customerName: string;
    partySize: number;
  };
  className?: string;
}

export const TableReservationIndicator: React.FC<TableReservationIndicatorProps> = ({
  reservation,
  className = '',
}) => {
  if (!reservation) return null;

  const statusColors: Record<ReservationStatus, { color: string; bgColor: string }> = {
    PENDING: { color: 'var(--status-warn-text)', bgColor: 'var(--status-warn-bg)' },
    CONFIRMED: { color: 'var(--action)', bgColor: '#CCFBF1' }, /* basil-todo */
    SEATED: { color: 'var(--status-ok)', bgColor: 'var(--status-ok-bg)' },
    COMPLETED: { color: 'var(--text-secondary)', bgColor: 'var(--bg-sunken)' },
    CANCELLED: { color: 'var(--status-bad)', bgColor: 'var(--status-bad-bg)' },
    NO_SHOW: { color: 'var(--status-bad-text)', bgColor: 'var(--status-bad-bg)' },
  };

  const { color, bgColor } = statusColors[reservation.status];

  return (
    <div
      className={`flex items-center gap-1 text-xs px-2 py-1 rounded-full ${className}`}
      style={{
        backgroundColor: bgColor,
        color: color,
      }}
    >
      <TimeIcon sx={{ fontSize: 12 }} />
      <span style={{ fontWeight: 600 }}>{reservation.startTime}</span>
      <span style={{ margin: '0 2px' }}>|</span>
      <PeopleIcon sx={{ fontSize: 12 }} />
      <span>{reservation.partySize}</span>
    </div>
  );
};

// ============================================================================
// MERGED TABLES INDICATOR
// ============================================================================

interface MergedTablesIndicatorProps {
  tableNames: string[];
  className?: string;
}

export const MergedTablesIndicator: React.FC<MergedTablesIndicatorProps> = ({
  tableNames,
  className = '',
}) => {
  if (tableNames.length <= 1) return null;

  return (
    <div
      className={`flex items-center gap-1 text-xs px-2 py-1 rounded-full ${className}`}
      style={{
        backgroundColor: 'var(--accent-alt-bg)',
        color: 'var(--accent-alt)',
      }}
    >
      <span style={{ fontWeight: 600 }}>Merged: {tableNames.join(' + ')}</span>
    </div>
  );
};

export default ReservationBadge;
